import type { ByIdsResultJSON } from "_shared/_api/by-ids/by-ids-result";
import { LOADING_PERSON, personStates, targetStates, unnamedPerson } from "permissions/_views/wording/permission-wording";

/**
 * What a row knows about the record or person it names (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Target and person states").
 *
 * A lookup answers each id it was asked about with `ByIdsResult<T>`: found, `missing` or
 * `unauthorized`. Those three, a lookup still under way and a lookup that failed are the five states
 * a row can be in. No row ever says "Unknown", "Unavailable" or "(Error: …)".
 */
export type NameState =
  | Readonly<{ state: "loading" }>
  | Readonly<{ state: "found"; name: string }>
  | Readonly<{ state: "missing" }>
  | Readonly<{ state: "unauthorized" }>
  | Readonly<{ state: "failed" }>;

const LOADING: NameState = { state: "loading" };
const FAILED: NameState = { state: "failed" };

/**
 * Each id's state from one by-ids lookup. An id the answer neither found nor listed as unavailable
 * would be a server that broke the contract; it is drawn as a failed lookup, which offers Retry,
 * rather than guessed at.
 */
export function nameStates(
  ids: readonly string[],
  lookup: Readonly<{ data?: ByIdsResultJSON<{ id: string; name: string }>; isError: boolean }>,
): ReadonlyMap<string, NameState> {
  const states = new Map<string, NameState>();
  if (lookup.isError) {
    for (const id of ids) states.set(id, FAILED);
    return states;
  }
  // No answer yet, and no failure: the lookup is still under way.
  if (!lookup.data) {
    for (const id of ids) states.set(id, LOADING);
    return states;
  }
  const found = new Map(lookup.data.found.map((record) => [record.id.toLowerCase(), record.name]));
  const unavailable = new Map(lookup.data.unavailable.map((entry) => [entry.id.toLowerCase(), entry.reason]));
  for (const id of ids) {
    const key = id.toLowerCase();
    const name = found.get(key);
    const reason = unavailable.get(key);
    if (name !== undefined) states.set(id, { state: "found", name });
    else if (reason) states.set(id, { state: reason });
    else states.set(id, FAILED);
  }
  return states;
}

/** The words a record row shows for a state other than found, in the kind's noun. */
export function targetStateText(state: NameState, noun: string): string | undefined {
  switch (state.state) {
    case "missing":
      return targetStates.missing(noun);
    case "unauthorized":
      return targetStates.unauthorized(noun);
    case "failed":
      return targetStates.failed(noun);
    default:
      return undefined;
  }
}

/** The words a person shows for a state other than found. */
export function personStateText(state: NameState): string | undefined {
  switch (state.state) {
    case "missing":
      return personStates.missing;
    case "unauthorized":
      return personStates.unauthorized;
    case "failed":
      return personStates.failed;
    default:
      return undefined;
  }
}

/** A person's name where the text cannot be skeleton text: the name, the state, or "Loading this person…". */
export function personLabel(state: NameState): string {
  if (state.state === "found") return state.name;
  return personStateText(state) ?? LOADING_PERSON;
}

/**
 * How a sentence or an accessible name refers to a person: their name, or a phrase that reads in the
 * name's place ("a former member"). Never the state sentence, which is not a name.
 */
export function personReference(state: NameState): string {
  switch (state.state) {
    case "found":
      return state.name;
    case "missing":
      return unnamedPerson.missing;
    case "unauthorized":
      return unnamedPerson.unauthorized;
    default:
      return unnamedPerson.unknown;
  }
}
